import { C } from "@fullcalendar/core/internal-common";
import { useFormik } from "formik";
import { getAzureBlobStorageBackend } from "helpers/azure_helper";
import { useEffect, useMemo, useRef, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setIsLoading } from "slices/jewellery/reducer";
import {
  updateJewellery as onUpdateJewellery,
  addJewellery as onAddJewellery,
} from "slices/thunk";
import {
  findObjectFromArrayByFieldAndValue,
  isStringArrayUpdated,
} from "utils/array";
import { formatBytes, getFileExtensionFromMimeType } from "utils/file";
import { allKeysHaveValues, compareObjects } from "utils/object";
import * as Yup from "yup";

type eventDataType = {
  _id?: string;
  title: string;
  sku: string;
  category: string;
  subcategory: string;
  gender: string;
  description: string;
  status: string;
  shape: string;
  visibility: string;
  media: any[];
  metal: {
    "14k": Number | null;
    "18k": Number | null;
    platinum: Number | null;
  };
  diamonds: diamondType[];
  tags: string[];
  bandWidth: number | null;
};

export type jewelleryMediaType = {
  id: string;
  type: string;
  label: string;
  files: [];
  images: string[];
};

export type jewellerySpecOptionType = { value: string; label: string };

export type jewellerySpecType = {
  type: "category" | "subcategory" | "shape" | "gender" | "status";
  label: string;
  options: jewellerySpecOptionType[];
};

type mediaEventDataType = {
  index: number;
  fileIndex: number;
};

export type diamondType = {
  _id: string;
  shape: string;
  color: string;
  clarity: string;
  carat: number;
  pieces: number;
};

const defaultCategoriesOptions = [{ value: "", label: "Select category" }];

const defaultSubCategoriesOptions = [
  { value: "", label: "Select sub category" },
];

const defaultShapesOptions = [{ value: "", label: "Select shape" }];

const genderOptions = [
  { value: "", label: "Select Gender" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "unisex", label: "Unisex" },
];

const statusOptions = [
  { value: "", label: "Select Status" },
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "published", label: "Published" },
];

// Jewellery specs default state

const defaultJewellerySpecs: jewellerySpecType[] = [
  { type: "category", label: "Category", options: [] },
  { type: "subcategory", label: "Sub Category", options: [] },
  { type: "shape", label: "Shape", options: [] },
  { type: "gender", label: "Gender", options: genderOptions },
  { type: "status", label: "Status", options: statusOptions },
];

const defaultJewelleryMedia: jewelleryMediaType[] = [
  { id: "", type: "yellowgold", label: "Yellow Gold", files: [], images: [] },
  { id: "", type: "rosegold", label: "Rose Gold", files: [], images: [] },
  { id: "", type: "whitegold", label: "White Gold", files: [], images: [] },
];

interface useAddJewelleryProps {
  jewelleryMedia: jewelleryMediaType[];
  setJewelleryMedia: (val: jewelleryMediaType[]) => void;
  deletedImagesUrls: string[];
  setDeletedImagesUrls: (val: string[]) => void;
}

const useAddJewellery = ({
  jewelleryMedia = [],
  setJewelleryMedia,
  deletedImagesUrls,
  setDeletedImagesUrls,
}: useAddJewelleryProps) => {
  const [eventData, setEventData] = useState<eventDataType>();
  const [isShowDeleteModel, setIsShowDeleteModel] = useState<boolean>(false);
  const [jewellerySpecs, setJewellerySpecs] = useState<jewellerySpecType[]>(
    defaultJewellerySpecs
  );
  // const categoriesToShowBandWidth = [
  //   "66c4b5e990a0baf60f9df01d", // Wedding Ring
  //   "66d825994c0f17e33815be68", // Engagement Ring
  // ];

  // const [jewelleryMedia, setJewelleryMedia] = useState<jewelleryMediaType[]>(
  //   defaultJewelleryMedia
  // );
  const jewelleryMediaRef = useRef<jewelleryMediaType[]>([]);
  jewelleryMediaRef.current = jewelleryMedia;
  const [mediaEventData, setMediaEventData] = useState<mediaEventDataType>();
  const [clickOnCreate, setClickOnCreate] = useState<boolean>(false);
  const [clickOnUpdate, setClickOnUpdate] = useState<boolean>(false);
  const [clickOnDraft, setClickOnDraft] = useState<boolean>(false);
  const [isProductDataUpdated, setIsProductDataUpdated] =
    useState<boolean>(false);
  const [isShowError, setIsShowError] = useState<boolean>(false);

  // For handle diamondS
  const [diamonds, setDiamonds] = useState<diamondType[]>([]);
  const [isDiamondsUpdated, setIsDiamondsUpdated] = useState<boolean>(false);
  const [diamondEventData, setDiamondEventData] = useState<diamondType | null>(
    null
  );
  const [deleteDiamondIndex, setDeleteDiamondIndex] = useState<number>();
  const [isShowDeleteDiamondConfirmModel, setIsShowDeleteDiamondConfirmModel] =
    useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(false);
  // const [deletedImagesUrls, setDeletedImagesUrls] = useState<string[]>([]);

  const { categories, isEditing, isLoading, jewellery, isDuplicate } =
    useSelector((state: any) => state.Jewellery);

  const { shapes } = useSelector((state: any) => state.Diamond);

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const categoriesToShowBandWidth: string[] = []; // Initialize an empty array for the ids

  categories.forEach((category: any) => {
    if (category.showBandWidth === true) {
      categoriesToShowBandWidth.push(category._id); // Add the category ID if showBandWidth is true
    }
  });

  //   Jewellery Specs
  const handleJewelleryCategoryOptions = () => {
    let clonedJewellerySpecs = [...jewellerySpecs];
    if (categories?.length) {
      const categoryIndex = clonedJewellerySpecs.findIndex(
        (spec) => spec.type === "category"
      );
      const categoriesOptions: jewellerySpecOptionType[] = categories.map(
        (category: { name: string; _id: string }) => {
          return { value: category._id, label: category.name };
        }
      );
      clonedJewellerySpecs[categoryIndex].options = [
        ...defaultCategoriesOptions,
        ...categoriesOptions,
      ];
      setJewellerySpecs(clonedJewellerySpecs);
    }
  };

  const handleJewellerySubCategoryOptions = (categoryId: string) => {
    let clonedJewellerySpecs = [...jewellerySpecs];
    const subcategoryIndex = clonedJewellerySpecs.findIndex(
      (spec) => spec.type === "subcategory"
    );
    const category = categories.find(
      (category: { _id: string; name: string }) =>
        category._id === categoryId || category.name === categoryId
    );
    const subcategoriesOptions = category?.subcategories.map(
      (subcategory: { name: string; _id: string }) => {
        return { value: subcategory._id, label: subcategory.name };
      }
    );
    clonedJewellerySpecs[subcategoryIndex].options = [
      ...defaultSubCategoriesOptions,
      ...subcategoriesOptions,
    ];
    setJewellerySpecs(clonedJewellerySpecs);
  };

  const handleShapesOptions = () => {
    let clonedJewellerySpecs = [...jewellerySpecs];
    if (shapes?.length) {
      const shapeIndex = clonedJewellerySpecs.findIndex(
        (spec) => spec.type === "shape"
      );
      const shapesOptions: jewellerySpecOptionType[] = shapes.map(
        (shape: { shape: string; _id: string }) => {
          return { value: shape._id, label: shape.shape };
        }
      );
      clonedJewellerySpecs[shapeIndex].options = [
        ...defaultShapesOptions,
        ...shapesOptions,
      ];
      setJewellerySpecs(clonedJewellerySpecs);
    }
  };

  const findMediaByType = (type: string) => {
    if (jewelleryMedia) {
      const media = jewelleryMedia.find(
        (product: jewelleryMediaType) => product.type === type
      );
      const files = media?.files.map((file: any) => {
        return {
          blobName: file.blobName,
          mimeType: file.type,
          data: file.file,
        };
      });
      return files || [];
    }
  };

  const uploadImagesForAllMetals = async () => {
    const containerName =
      process.env.REACT_APP_AZURE_BLOB_JEWELLERY_IMAGES_CONTAINER || "";
    const goldMedia = findMediaByType("yellowgold");
    const rosegoldMedia = findMediaByType("rosegold");
    const whitegoldMedia = findMediaByType("whitegold");

    const azure = getAzureBlobStorageBackend();

    const goldPromise = azure?.uploadBlobs({
      containerName,
      files: goldMedia || [],
    });

    const rosegoldPromise = azure?.uploadBlobs({
      containerName,
      files: rosegoldMedia || [],
    });

    const whitegoldPromise = azure?.uploadBlobs({
      containerName,
      files: whitegoldMedia || [],
    });

    const [goldImages, rosegoldImages, whitegoldImages] = await Promise.all([
      goldPromise,
      rosegoldPromise,
      whitegoldPromise,
    ]);

    return {
      goldImages: goldImages || [],
      rosegoldImages: rosegoldImages || [],
      whitegoldImages: whitegoldImages || [],
    };
  };

  const handleDeleteJewelleryImage = async (
    index: number,
    fileIndex: number
  ) => {
    let clonedJewelleryMedia = jewelleryMedia.map((item) => ({ ...item }));

    if (isEditing) {
      const imageUrl = clonedJewelleryMedia[index].images[fileIndex];

      setDeletedImagesUrls([...deletedImagesUrls, imageUrl]);

      clonedJewelleryMedia[index].images = clonedJewelleryMedia[
        index
      ].images.filter((_, i) => i !== fileIndex);
    } else {
      clonedJewelleryMedia[index].files.splice(fileIndex, 1);
    }
    setJewelleryMedia([...clonedJewelleryMedia]);
    setIsShowDeleteModel(false);
  };

  const handleDelete = async () => {
    if (
      mediaEventData?.index !== undefined &&
      mediaEventData?.fileIndex !== undefined
    ) {
      await handleDeleteJewelleryImage(
        mediaEventData.index,
        mediaEventData.fileIndex
      );
    }
  };

  const handleDeleteDiamond = () => {
    let clonedDiamonds = [...diamonds];
    if (deleteDiamondIndex !== undefined) {
      clonedDiamonds.splice(deleteDiamondIndex, 1);
    }
    setDiamonds(clonedDiamonds);
    setIsShowDeleteDiamondConfirmModel(false);
  };

  const initialValues = useMemo(
    () => ({
      sku: eventData?.sku || "",
      title: eventData?.title || "",
      category: eventData?.category || "",
      subcategory: eventData?.subcategory || "",
      shape: eventData?.shape || "",
      gender: eventData?.gender || "",
      description: eventData?.description || "",
      status: eventData?.status || "",
      visibility: eventData?.visibility || "public",
      metal: {
        "14k": eventData?.metal?.["14k"] || "",
        "18k": eventData?.metal?.["18k"] || "",
        platinum: eventData?.metal?.platinum || "",
      },
      tags: eventData?.tags || [],
      bandWidth: eventData?.bandWidth || null,
    }),
    [eventData]
  );

  const addJewelleryForm: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues,

    validationSchema: Yup.object({
      sku: Yup.string().required("Please Enter sku").max(100),
      title: Yup.string().required("Please Enter title").max(100),
      category: Yup.string().required("Please Select category"),
      subcategory: Yup.string().required("Please Select subcategory"),
      shape: Yup.string().required("Please Select shape"),
      gender: Yup.string().required("Please Select gender"),
      description: Yup.string().required("Please Enter description"),
      status: Yup.string().required("Please Select status"),
      // visibility: Yup.string().required("Please Select visibility"),
      metal: Yup.object().shape({
        "14k": Yup.string().required("Please Enter weight for 14k gold"),
        "18k": Yup.string().required("Please Enter weight for 18k gold"),
        platinum: Yup.string().required("Please Enter weight for platinum"),
      }),
      tags: Yup.array().of(
        Yup.string().required("Each element must be a non-empty string")
      ),
      bandWidth: Yup.string().when("category", ([category]) => {
        if (categoriesToShowBandWidth.includes(category)) {
          return Yup.string().required("Please Enter band width");
        }
        return Yup.string().nullable();
      }),
    }),

    onSubmit: async (values) => {
      const formData: any = new FormData();
      // Assuming jewelleryMedia is an array of media objects with files
      const mappedMedia: { [key: string]: File[] } = {};

      const typesMapping: any = {
        goldImage: "yellowgold",
        roseGoldImage: "rosegold",
        whiteGoldImage: "whitegold",
      };


      // Begin the dispatch logic
      dispatch(setIsLoading(true));

      if (isEditing) {
        const goldMedia = findObjectFromArrayByFieldAndValue(
          jewelleryMedia,
          "type",
          "yellowgold"
        );
        const rosegoldMedia = findObjectFromArrayByFieldAndValue(
          jewelleryMedia,
          "type",
          "rosegold"
        );
        const whitegoldMedia = findObjectFromArrayByFieldAndValue(
          jewelleryMedia,
          "type",
          "whitegold"
        );

        let payload: any = {
          product: { id: eventData?._id, fields: { ...values } },
          medias: [
            {
              id: goldMedia.id,
              type: goldMedia.type,
              fields: {
                images: goldMedia.images,
              },
            },
            {
              id: rosegoldMedia.id,
              type: rosegoldMedia.type,
              fields: {
                images: rosegoldMedia.images,
              },
            },
            {
              id: whitegoldMedia.id,
              type: whitegoldMedia.type,
              fields: {
                images: whitegoldMedia.images,
              },
            },
          ],
          deletedImages: deletedImagesUrls,
        };

        if (isDiamondsUpdated) {
          payload.product.fields["diamonds"] = diamonds.map((diamond) => {
            const foundDiamond = eventData?.diamonds.find(
              (diamond_) => diamond._id === diamond_._id
            );
            if (foundDiamond) {
              return diamond;
            }
            return {
              shape: diamond.shape,
              color: diamond.color,
              clarity: diamond.clarity,
              carat: diamond.carat,
              pieces: diamond.pieces,
            };
          });
        }
        formData.append("payload", JSON.stringify(payload));

        // Iterate through each media item
        jewelleryMedia.forEach((mediaItem) => {
          if (mediaItem.files.length > 0 || mediaItem.images.length > 0) {
            // Determine the custom key for this media type
            const customKey = Object.keys(typesMapping).find(
              (key) => typesMapping[key] === mediaItem.type
            );

            if (customKey) {
              // Append new files (if any) to the FormData
              mediaItem.files.forEach((file: any) => {
                const blob = new Blob([file.file], { type: file.type });
                formData.append(`${customKey}`, blob);
              });
            }
          }
        });

        try {
          const result = await dispatch(onUpdateJewellery(formData));
          if (result.status === 200) {
            navigate("/jewellery");
          }
        } catch (error) {
          console.log(error);
        }
      } else {
        // const { goldImages, rosegoldImages, whitegoldImages } = await uploadImagesForAllMetals();

        let payload: any = {
          product: { ...values },
          // media: [
          //   {
          //     type: "gold",
          //     images: goldImages,
          //     videos: [],
          //   },
          //   {
          //     type: "rosegold",
          //     images: rosegoldImages,
          //     videos: [],
          //   },
          //   {
          //     type: "whitegold",
          //     images: whitegoldImages,
          //     videos: [],
          //   },
          // ],
        };

        if (diamonds.length) {
          payload.product["diamonds"] = diamonds.map((diamond) => {
            return {
              shape: diamond.shape,
              color: diamond.color,
              clarity: diamond.clarity,
              carat: diamond.carat,
              pieces: diamond.pieces,
            };
          });
        }
        formData.append("product", JSON.stringify(payload.product));

        jewelleryMedia.forEach((mediaItem) => {
          if (mediaItem.files.length > 0) {
            // Loop through files and map them based on the type
            mediaItem.files.forEach((file: any, index: number) => {
              const customKey = Object.keys(typesMapping).find(
                (key) => typesMapping[key] === mediaItem.type // Compare mediaItem.type with the values of typesMapping
              );

              if (customKey) {  
                if (!mappedMedia[customKey]) {
                  mappedMedia[customKey] = [];
                }
                mappedMedia[customKey].push(file);
              }

              const blob = new Blob([file.file], { type: file.type });
              formData.append(`${customKey}`, blob);
            });
          }
        });

        try {
          // Use the FormData as payload to dispatch for adding jewellery
          const result = await dispatch(onAddJewellery(formData));
          if (result.status === 200) {
            navigate("/jewellery");
          }
        } catch (error) {
          console.log(error);
        }
      }
    },
  });

  const onDragEnd = (result: DropResult, index: number) => {
    if (!result.destination) return;

    const newFiles = Array.from(
      isEditing ? jewelleryMedia[index].images : jewelleryMedia[index].files
    );
    const [reorderedFile] = newFiles.splice(result.source.index, 1);
    newFiles.splice(result.destination.index, 0, reorderedFile);

    const newJewelleryMedia = [...jewelleryMedia];
    if (isEditing) {
      newJewelleryMedia[index] = {
        ...newJewelleryMedia[index],
        images: newFiles as any,
      };
    } else {
      newJewelleryMedia[index] = {
        ...newJewelleryMedia[index],
        files: newFiles as any,
      };
    }
    setJewelleryMedia([...newJewelleryMedia]);
  };

  const uploadProductImages = async (data: any) => {
    const containerName =
      process.env.REACT_APP_AZURE_BLOB_JEWELLERY_IMAGES_CONTAINER || "";
    const azure = getAzureBlobStorageBackend();
    const files = data.map((file: any) => {
      return {
        blobName: file.blobName,
        mimeType: file.type,
        data: file.file,
      };
    });
    const images = await azure?.uploadBlobs({ containerName, files });
    return images || [];
  };

  const handleAcceptfiles = async (files: any, index: number) => {
    files?.map((file: File) => {
      const fileExtension = getFileExtensionFromMimeType(file.type, file.name);
      return Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
        file: new Blob([file], { type: file.type }),
        blobName: `${Date.now()}${fileExtension ? "." + fileExtension : ""}`,
      });
    });

    let clonedJewelleryMedia = [...jewelleryMedia];
    clonedJewelleryMedia[index].files = [
      ...clonedJewelleryMedia[index].files,
      ...files,
    ] as any;
    setJewelleryMedia([...clonedJewelleryMedia]);
  };

  const handleUpdateDiamondClick = (data: diamondType) => {
    setDiamondEventData(data);
    setIsEdit(true);
  };

  const checkIsProductDataUpdated = () => {
    if (addJewelleryForm && eventData) {
      let isValueUpdated = false;
      let isMediaUpdated = false;
      let isMetalUpdated = false;
      let isDiamondsUpdated = false;
      let formValues = addJewelleryForm.values;
      let clonedEventData: any = eventData;
      if (formValues.bandWidth === null) {
        delete formValues.bandWidth;
        delete clonedEventData.bandWidth;
      }
      const isAllKeysHasValue = allKeysHaveValues(formValues);
      // First object must me that which key value should be compare
      if (isAllKeysHasValue) {
        isValueUpdated = compareObjects(formValues, clonedEventData);
        isMetalUpdated = compareObjects(
          formValues.metal,
          clonedEventData.metal
        );
      }
      if (jewelleryMedia) {
        const productOldMedia = eventData?.media;
        isMediaUpdated = productOldMedia.some((media) => {
          const currentMedia = jewelleryMedia.find(
            (media_) => media_.type === media.type
          );
          if (currentMedia?.images || currentMedia?.files) {
            const isUpdated = isStringArrayUpdated(
              media.images,
              currentMedia.images
            );
            const isAnyNewImageUploaded = currentMedia.files.length > 0;
            return isUpdated || isAnyNewImageUploaded;
          }
        });
      }
      if (diamonds) {
        if (diamonds.length !== eventData.diamonds.length) {
          isDiamondsUpdated = true;
        } else {
          isDiamondsUpdated = eventData.diamonds.some((diamond) => {
            const foundDiamond = diamonds.find(
              (diamond_) => diamond._id === diamond_._id
            );
            if (foundDiamond) {
              return compareObjects(foundDiamond, diamond);
            } else {
              return true;
            }
          });
        }
      }
      isDiamondsUpdated && setIsDiamondsUpdated(true);
      if (
        (isMediaUpdated ||
          isValueUpdated ||
          isMetalUpdated ||
          isDiamondsUpdated) !== isProductDataUpdated
      ) {
        setIsProductDataUpdated(
          isMediaUpdated ||
            isValueUpdated ||
            isMetalUpdated ||
            isDiamondsUpdated
        );
      }
    }
  };

  useEffect(() => {
    if (!isEditing) {
      setDiamonds([]); // Reset diamonds when not editing
    }
  }, [isEditing]);

  const handleSetProductData = () => {
    const jewelleryMedia = defaultJewelleryMedia.map((jewelleryMedia) => {
      const data = findObjectFromArrayByFieldAndValue(
        jewellery?.media,
        "type",
        jewelleryMedia?.type
      );
      return {
        ...jewelleryMedia,
        id: data?._id || null,
        images: data?.images,
      };
    });

    const category = categories.find(
      (cat: any) => cat.name === jewellery.category
    );
    const categoryId = category ? category._id : null;
    const subcategory = category?.subcategories.find(
      (subcat: any) => subcat.name === jewellery.subcategory
    );
    const shape = shapes.find((shape: any) => shape.shape === jewellery.shape);
    handleJewellerySubCategoryOptions(categoryId);
    setEventData({
      ...jewellery,
      category: category?._id || "",
      subcategory: subcategory?._id || "",
      shape: shape?._id || "",
    });
    setJewelleryMedia([...jewelleryMedia]);
    setDiamonds(jewellery.diamonds);
  };

  const handleDuplicateData = () => {
    const duplicatedJewellery = {
      ...jewellery,
      title: `${jewellery.title} copy`,
      sku: `${jewellery.sku}-copy`,
    };
    handleJewellerySubCategoryOptions(duplicatedJewellery.category);
    setEventData(duplicatedJewellery);
    setDiamonds(duplicatedJewellery.diamonds);
  };

  return {
    // isShowError,
    // setIsShowError,
    setDeletedImagesUrls,
    isDuplicate,
    isEditing,
    isLoading,
    isShowDeleteModel,
    setIsShowDeleteModel,
    handleDelete,
    addJewelleryForm,
    jewellerySpecs,
    handleJewelleryCategoryOptions,
    categories,
    jewelleryMedia,
    onDragEnd,
    handleAcceptfiles,
    setMediaEventData,
    diamonds,
    shapes,
    isEdit,
    setIsEdit,
    handleUpdateDiamondClick,
    setDiamonds,
    diamondEventData,
    setDiamondEventData,
    clickOnCreate,
    setClickOnCreate,
    clickOnUpdate,
    setClickOnUpdate,
    clickOnDraft,
    setClickOnDraft,
    isProductDataUpdated,
    checkIsProductDataUpdated,
    jewellery,
    eventData,
    setEventData,
    setJewelleryMedia,
    handleSetProductData,
    handleShapesOptions,
    handleJewellerySubCategoryOptions,
    show,
    setShow,
    isShowDeleteDiamondConfirmModel,
    setIsShowDeleteDiamondConfirmModel,
    handleDeleteDiamond,
    setDeleteDiamondIndex,
    categoriesToShowBandWidth,
    handleDuplicateData,
    defaultJewelleryMedia,
    defaultSubCategoriesOptions,
  };
};

export default useAddJewellery;
